import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Clock, UserCheck, Briefcase, DollarSign, Download, Printer, Calendar,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const TABS = [
  { key: 'hours',       title: 'My Hours',          icon: Clock,       endpoint: '/reports/my/hours' },
  { key: 'attendance',  title: 'My Attendance',     icon: UserCheck,   endpoint: '/reports/my/attendance' },
  { key: 'jobs',        title: 'My Completed Jobs', icon: Briefcase,   endpoint: '/reports/my/completed-jobs' },
  { key: 'payroll',     title: 'My Payroll',        icon: DollarSign,  endpoint: '/reports/my/payroll' },
];

function ymd(d) { return d.toISOString().slice(0, 10); }

function rangePreset(preset) {
  const now = new Date();
  if (preset === 'this_week') {
    const day = now.getUTCDay() || 7;
    const start = new Date(now); start.setUTCDate(now.getUTCDate() - (day - 1));
    return { start: ymd(start), end: ymd(now) };
  }
  if (preset === 'this_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { start: ymd(start), end: ymd(now) };
  }
  if (preset === 'last_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
    return { start: ymd(start), end: ymd(end) };
  }
  if (preset === 'last_3_months') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1));
    return { start: ymd(start), end: ymd(now) };
  }
  return { start: ymd(now), end: ymd(now) };
}

function downloadCSV(filename, rows, columns) {
  if (!rows?.length) return;
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map((c) => c.label).join(',');
  const body = rows.map((r) => columns.map((c) => esc(typeof c.value === 'function' ? c.value(r) : r[c.key])).join(',')).join('\n');
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function printHtml(title, innerHtml) {
  const w = window.open('', '_blank');
  if (!w) { window.print(); return; }
  const css = `
    *{box-sizing:border-box} body{font-family:Inter,Arial,sans-serif;background:#fff;color:#111;padding:24px}
    h1{margin:0 0 16px 0;font-size:22px}
    table{border-collapse:collapse;width:100%;font-size:12px}
    th,td{border:1px solid #ddd;padding:6px 10px;text-align:left}
    th{background:#f5f5f5}
    @page{size:landscape}
  `;
  // Build the popup DOM via standard DOM APIs instead of document.write,
  // which is flagged as an XSS-risk pattern even when the inputs are trusted.
  w.document.title = title;
  const styleEl = w.document.createElement('style');
  styleEl.textContent = css;
  w.document.head.appendChild(styleEl);
  const h1 = w.document.createElement('h1');
  h1.textContent = title;
  w.document.body.appendChild(h1);
  const container = w.document.createElement('div');
  container.innerHTML = innerHtml;
  w.document.body.appendChild(container);
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 300);
}

export default function StaffReports() {
  const { token, user } = useAuth();
  const [activeKey, setActiveKey] = useState('hours');
  const [preset, setPreset] = useState('this_month');
  const [rng, setRng] = useState(() => rangePreset('this_month'));
  const [customOpen, setCustomOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  const activeTab = TABS.find((t) => t.key === activeKey);

  const fetchData = useCallback(async () => {
    if (!token || !activeTab) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}${activeTab.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start_date: rng.start, end_date: rng.end },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, rng.start, rng.end]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setPresetAndRange = (p) => {
    setPreset(p);
    setRng(rangePreset(p));
    setCustomOpen(false);
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  const exportCurrent = () => {
    const name = `${activeTab.key}_${rng.start}_to_${rng.end}.csv`;
    let columns = [];
    if (activeKey === 'hours')       columns = [{key:'date',label:'Date'},{key:'clock_ins',label:'Clock-ins'},{key:'hours',label:'Hours'}];
    if (activeKey === 'attendance')  columns = [{key:'date',label:'Date'},{key:'status',label:'Status'},{key:'first_clock_in',label:'First Clock-in'}];
    if (activeKey === 'jobs')        columns = [{key:'title',label:'Job'},{key:'customer',label:'Customer'},{key:'completed_at',label:'Completed At'},{key:'hours_worked',label:'Hours'},{key:'service_rate',label:'Rate'}];
    if (activeKey === 'payroll')     columns = [{key:'date',label:'Date'},{key:'hours',label:'Hours'},{key:'rate',label:'Rate'},{key:'pay',label:'Pay'}];
    downloadCSV(name, rows, columns);
  };

  const onPrint = () => {
    const html = containerRef.current?.innerHTML || '';
    printHtml(`${activeTab.title} — ${rng.start} to ${rng.end}`, html);
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="staff-reports">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white">My Reports</h1>
          <p className="text-sm mt-0.5" style={{ color: '#A0AEC0' }}>
            Personal reports for {user?.name || 'you'} — hours, attendance, completed jobs, and payroll.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCurrent} disabled={!rows.length} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white disabled:opacity-40" style={{ background: '#00C896' }} data-testid="my-reports-export-csv">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button onClick={onPrint} disabled={!rows.length} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white disabled:opacity-40" style={{ background: '#1E88E5' }} data-testid="my-reports-print">
            <Printer className="w-3.5 h-3.5" /> Print / PDF
          </button>
        </div>
      </div>

      {/* Date range presets */}
      <div className="ht-card p-3 flex flex-wrap items-center gap-2" data-testid="my-reports-range-bar">
        {[
          { k: 'this_week',     label: 'This Week' },
          { k: 'this_month',    label: 'This Month' },
          { k: 'last_month',    label: 'Last Month' },
          { k: 'last_3_months', label: 'Last 3 Months' },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setPresetAndRange(p.k)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{
              background: preset === p.k ? '#1E88E5' : 'transparent',
              color: preset === p.k ? '#fff' : '#A0AEC0',
              border: `1px solid ${preset === p.k ? '#1E88E5' : '#2D3748'}`,
            }}
            data-testid={`my-reports-preset-${p.k}`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setCustomOpen((o) => !o)}
          className="px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5"
          style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}
          data-testid="my-reports-preset-custom"
        >
          <Calendar className="w-3.5 h-3.5" />
          Custom
        </button>
        {customOpen && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={rng.start} onChange={(e) => setRng({ ...rng, start: e.target.value })} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }} data-testid="my-reports-start-date" />
            <span style={{ color: '#64748B' }}>→</span>
            <input type="date" value={rng.end} onChange={(e) => setRng({ ...rng, end: e.target.value })} className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#fff', border: '1px solid #2D3748' }} data-testid="my-reports-end-date" />
          </div>
        )}
        <span className="ml-auto text-xs" style={{ color: '#64748B' }}>
          {rng.start} → {rng.end}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="my-reports-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeKey === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveKey(t.key)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors"
              style={{
                background: active ? '#1E88E5' : '#1A2433',
                color: active ? '#fff' : '#A0AEC0',
                border: `1px solid ${active ? '#1E88E5' : '#2D3748'}`,
              }}
              data-testid={`my-reports-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              {t.title}
            </button>
          );
        })}
      </div>

      {/* Totals summary */}
      {!loading && Object.keys(totals).length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="my-reports-totals">
          {Object.entries(totals).map(([k, v]) => (
            <div key={k} className="ht-card p-3">
              <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>
                {k.replace(/_/g, ' ')}
              </p>
              <p className="text-xl font-bold text-white mt-1">
                {typeof v === 'number' && (k.includes('pay') || k.includes('rate')) ? `$${v.toFixed(2)}` : String(v)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="ht-card p-4" ref={containerRef} data-testid="my-reports-table-wrap">
        <h2 className="text-white font-semibold mb-3">{activeTab.title}</h2>
        {loading ? (
          <p className="text-sm" style={{ color: '#A0AEC0' }}>Loading…</p>
        ) : error ? (
          <p className="text-sm" style={{ color: '#EF4444' }} data-testid="my-reports-error">{error}</p>
        ) : !rows.length ? (
          <p className="text-sm" style={{ color: '#64748B' }} data-testid="my-reports-empty">No data for this date range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="my-reports-table">
              <thead>
                <tr style={{ borderBottom: '1px solid #2D3748' }}>
                  {activeKey === 'hours' && ['Date', 'Clock-ins', 'Hours'].map((h) => (
                    <th key={h} className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>{h}</th>
                  ))}
                  {activeKey === 'attendance' && ['Date', 'Status', 'First Clock-in'].map((h) => (
                    <th key={h} className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>{h}</th>
                  ))}
                  {activeKey === 'jobs' && ['Job', 'Customer', 'Completed At', 'Hours', 'Rate'].map((h) => (
                    <th key={h} className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>{h}</th>
                  ))}
                  {activeKey === 'payroll' && ['Date', 'Hours', 'Rate', 'Pay'].map((h) => (
                    <th key={h} className="text-left py-2 px-2 font-medium" style={{ color: '#64748B' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.date || r.id || `row-${i}`} style={{ borderBottom: '1px solid #1F2D3F' }}>
                    {activeKey === 'hours' && (
                      <>
                        <td className="py-2 px-2 text-white">{r.date}</td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>{r.clock_ins}</td>
                        <td className="py-2 px-2 text-white font-medium">{r.hours}</td>
                      </>
                    )}
                    {activeKey === 'attendance' && (
                      <>
                        <td className="py-2 px-2 text-white">{r.date}</td>
                        <td className="py-2 px-2">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide" style={{
                            background: r.status === 'present' ? 'rgba(0,200,150,0.15)' : r.status === 'late' ? 'rgba(255,160,0,0.15)' : 'rgba(239,68,68,0.15)',
                            color:      r.status === 'present' ? '#00C896' :                r.status === 'late' ? '#FFA000' :                '#EF4444',
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>{r.first_clock_in || '—'}</td>
                      </>
                    )}
                    {activeKey === 'jobs' && (
                      <>
                        <td className="py-2 px-2 text-white">{r.title}</td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>{r.customer || '—'}</td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>
                          {r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}
                        </td>
                        <td className="py-2 px-2 text-white font-medium">{r.hours_worked}</td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>{r.service_rate ? `$${r.service_rate}` : '—'}</td>
                      </>
                    )}
                    {activeKey === 'payroll' && (
                      <>
                        <td className="py-2 px-2 text-white">{r.date}</td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>{r.hours}</td>
                        <td className="py-2 px-2" style={{ color: '#A0AEC0' }}>{r.rate ? `$${r.rate}` : '—'}</td>
                        <td className="py-2 px-2 text-white font-medium">{r.pay ? `$${r.pay}` : '—'}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
